/**
 * キーボードに重ねる「半透明の手」の形(docs/02_game_design.md §3 の指の透過表示)。
 *
 * 参考画像(修正・参考/タイピング画面参考.png)の輪郭線の手を SVG で自作する:
 *  - 指はホームポジションのキーに指先を置いた輪郭線
 *  - 次に打つキーを担当する指だけ、そのキーの中心まで指先を伸ばして強調する
 *  - 手のひらは下からはみ出す(viewBox の外は SVG が自動でクリップする)
 *
 * 座標系は `logic/keyboardLayout.ts` と同じ(= キーボードSVGの viewBox)。
 * 描画非依存の純関数なので単体テストできる。
 */

import { bindingForChar, type FingerId } from './keyboard';
import { homeCenter, keyCenter, KEYBOARD_LAYOUT, type Point } from './keyboardLayout';

export type HandSide = 'left' | 'right';

export interface FingerSpec {
  readonly id: FingerId;
  readonly side: HandSide;
  /** 指の付け根(手のひら側)。 */
  readonly base: Point;
  /** 付け根の半幅。 */
  readonly baseHalf: number;
  /** 指先の半幅(= 指先の丸みの半径)。 */
  readonly tipHalf: number;
  /** ホームポジションの指先。 */
  readonly home: Point;
}

/** 手のひらの矩形(角丸)。下端は viewBox の外に出してクリップさせる。 */
export interface PalmSpec {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly radius: number;
}

export interface HandSpec {
  readonly side: HandSide;
  readonly label: string;
  readonly palm: PalmSpec;
  readonly fingers: readonly FingerSpec[];
}

/** キーボードの左右中央(右手は左手の鏡像で作る)。 */
const CENTER_X = KEYBOARD_LAYOUT.width / 2;
/** 手のひらの上端(スペース段に重なる高さ)。 */
const PALM_TOP = 300;
/** 手のひらの高さ。viewBox(高さ 334)より下は切れて「下から出ている手」に見える。 */
const PALM_HEIGHT = 180;

/** 親指の指先(スペースバー上)。左右で少しずらして重ならないようにする。 */
const SPACE_CENTER = keyCenter('Space') ?? { x: CENTER_X, y: 303 };
const THUMB_OFFSET = 42;
/** 親指の付け根の、いちばん外側の指の付け根からの距離。 */
const THUMB_BASE_GAP = 42;
/** 手のひらの幅。 */
const PALM_WIDTH = 220;

/**
 * 指の形。
 *
 * 付け根の位置は「ホームポジションのキーからの相対」で持つ。
 * JIS配列のホーム段は左右対称ではない(左は CapsLock、右は `:`/`]` がある)ので、
 * 絶対座標を鏡像にすると右手だけ指先とずれてしまう。
 *
 * `dx` は小指ほど外へ、人差し指はやや内へ。指を軽く扇形に開いた自然な形になる。
 */
const LONG_FINGERS: readonly {
  /** 小指から人差し指の順。 */
  readonly left: Exclude<FingerId, 'thumb'>;
  readonly right: Exclude<FingerId, 'thumb'>;
  /** 付け根の、ホームポジションからの横ずれ(左手基準。右手は反転)。 */
  readonly dx: number;
  readonly baseY: number;
  readonly baseHalf: number;
  readonly tipHalf: number;
}[] = [
  { left: 'l-pinky', right: 'r-pinky', dx: 34.8, baseY: 322, baseHalf: 15, tipHalf: 12.5 },
  { left: 'l-ring', right: 'r-ring', dx: 20.8, baseY: 330, baseHalf: 17, tipHalf: 14.5 },
  { left: 'l-middle', right: 'r-middle', dx: 6.8, baseY: 334, baseHalf: 17.5, tipHalf: 15 },
  { left: 'l-index', right: 'r-index', dx: -7.2, baseY: 330, baseHalf: 18, tipHalf: 15 },
];

function buildHand(side: HandSide, label: string): HandSpec {
  // 左手は指先の右側へ、右手は左側へ手のひらが来る(= dx の向きが逆)。
  const sign = side === 'left' ? 1 : -1;
  const long = LONG_FINGERS.map((finger): FingerSpec => {
    const id = side === 'left' ? finger.left : finger.right;
    const home = homeCenter(id);
    return {
      id,
      side,
      base: { x: home.x + sign * finger.dx, y: finger.baseY },
      baseHalf: finger.baseHalf,
      tipHalf: finger.tipHalf,
      home,
    };
  });

  const baseXs = long.map((finger) => finger.base.x);
  // 親指は「いちばん内側(スペース寄り)の指」のさらに内側から生える。
  const thumbBaseX =
    side === 'left' ? Math.max(...baseXs) + THUMB_BASE_GAP : Math.min(...baseXs) - THUMB_BASE_GAP;
  const thumb: FingerSpec = {
    id: 'thumb',
    side,
    base: { x: thumbBaseX, y: 372 },
    baseHalf: 23,
    tipHalf: 18,
    home: { x: SPACE_CENTER.x + sign * -THUMB_OFFSET, y: SPACE_CENTER.y + 2 },
  };

  const palmCenter = baseXs.reduce((sum, x) => sum + x, 0) / baseXs.length;
  return {
    side,
    label,
    palm: {
      x: palmCenter - PALM_WIDTH / 2,
      y: PALM_TOP,
      width: PALM_WIDTH,
      height: PALM_HEIGHT,
      radius: 64,
    },
    fingers: [...long, thumb],
  };
}

export const HAND_SPECS: readonly HandSpec[] = [
  buildHand('left', 'ひだりて'),
  buildHand('right', 'みぎて'),
];

const round = (value: number): string => (Math.round(value * 10) / 10).toString();

/**
 * 指1本の輪郭(付け根から指先までのカプセル)。
 *
 * 付け根側は手のひらに埋まるので直線で閉じる。単一パスの `d` に連結して
 * `fill-rule: nonzero` で塗ると、重なっても濃くならず半透明が一定になる。
 */
export function fingerPath(base: Point, tip: Point, baseHalf: number, tipHalf: number): string {
  const dx = tip.x - base.x;
  const dy = tip.y - base.y;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  // 進行方向に対する法線。
  const px = -uy;
  const py = ux;
  // 指先の丸み(円弧のかわりに三次ベジェ。sweep フラグの向きを気にしなくて済む)。
  const k = tipHalf * 1.35;

  const a = { x: base.x + px * baseHalf, y: base.y + py * baseHalf };
  const b = { x: tip.x + px * tipHalf, y: tip.y + py * tipHalf };
  const c = { x: tip.x - px * tipHalf, y: tip.y - py * tipHalf };
  const d = { x: base.x - px * baseHalf, y: base.y - py * baseHalf };

  return [
    `M${round(a.x)} ${round(a.y)}`,
    `L${round(b.x)} ${round(b.y)}`,
    `C${round(b.x + ux * k)} ${round(b.y + uy * k)} ${round(c.x + ux * k)} ${round(
      c.y + uy * k,
    )} ${round(c.x)} ${round(c.y)}`,
    `L${round(d.x)} ${round(d.y)}`,
    'Z',
  ].join(' ');
}

/** 手のひら(角丸長方形)の輪郭。 */
export function palmPath(palm: PalmSpec): string {
  const { x, y, width, height } = palm;
  const r = Math.min(palm.radius, width / 2, height / 2);
  return [
    `M${round(x + r)} ${round(y)}`,
    `H${round(x + width - r)}`,
    `A${round(r)} ${round(r)} 0 0 1 ${round(x + width)} ${round(y + r)}`,
    `V${round(y + height - r)}`,
    `A${round(r)} ${round(r)} 0 0 1 ${round(x + width - r)} ${round(y + height)}`,
    `H${round(x + r)}`,
    `A${round(r)} ${round(r)} 0 0 1 ${round(x)} ${round(y + height - r)}`,
    `V${round(y + r)}`,
    `A${round(r)} ${round(r)} 0 0 1 ${round(x + r)} ${round(y)}`,
    'Z',
  ].join(' ');
}

/**
 * 次に打つキーの位置を指ごとに引く。
 *
 * 同じ指に複数候補(si/shi の s と c など)があるときは、
 * ホームポジションからいちばん近いキーを選ぶ(指の動きが最小の打ち方を教える)。
 * 親指はスペースに置いたままなので動かさない。
 */
export function fingerTargets(nextKeys: readonly string[]): ReadonlyMap<FingerId, Point> {
  const targets = new Map<FingerId, Point>();
  for (const char of nextKeys) {
    const binding = bindingForChar(char);
    if (!binding || binding.finger === 'thumb') continue;
    const center = keyCenter(binding.keyId);
    if (!center) continue;
    const home = homeCenter(binding.finger);
    const previous = targets.get(binding.finger);
    if (previous && distance(previous, home) <= distance(center, home)) continue;
    targets.set(binding.finger, center);
  }
  return targets;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** その指の指先(強調中なら対象キー、そうでなければホームポジション)。 */
export function fingerTip(finger: FingerSpec, targets: ReadonlyMap<FingerId, Point>): Point {
  return targets.get(finger.id) ?? finger.home;
}

/** 手1つぶんの輪郭(手のひら+指をまとめた1本のパス)。 */
export function handPath(hand: HandSpec, targets: ReadonlyMap<FingerId, Point>): string {
  const parts = [palmPath(hand.palm)];
  for (const finger of hand.fingers) {
    parts.push(
      fingerPath(finger.base, fingerTip(finger, targets), finger.baseHalf, finger.tipHalf),
    );
  }
  return parts.join(' ');
}
